/**
 * Signed-in PWA host, mounted once by the shell (ShellFrame):
 *
 * - opens the install sheet by itself on phones and tablets after sign-in,
 *   when the snooze rules allow (snooze.ts), never on desktop and never
 *   inside the installed app;
 * - adds "Install app" to the user menu so the sheet can always be opened;
 * - shows "New version available" with Reload when a new build is waiting.
 */
import { toast } from '@velora/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { registerUserMenuItem } from '../layout/TopBarParts.js';
import { InstallSheet, sheetMode } from './InstallSheet.js';
import {
  currentDevice,
  currentStandalone,
  installPlatform,
  isMobileDevice,
  isTablet,
} from './platform.js';
import { applyUpdate, promptInstall, trackUiEvent, usePwaState } from './runtime.js';
import {
  dismiss,
  loadState,
  markInstalled,
  markShown,
  rememberShownThisSession,
  saveState,
  shouldAutoPrompt,
  shownThisSession,
} from './snooze.js';

/** Let the shell settle (and the Home page paint) before the sheet slides up. */
export const AUTO_PROMPT_DELAY_MS = 1500;

export function PwaHost() {
  const pwa = usePwaState();
  const device = useMemo(currentDevice, []);
  const platform = useMemo(() => installPlatform(device), [device]);
  const mobile = isMobileDevice(device);
  const [standalone, setStandalone] = useState(currentStandalone);
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState<'auto' | 'menu'>('auto');
  const mode = sheetMode(platform, pwa.deferredPrompt !== null);

  // display-mode flips to standalone when Android installs and opens the app.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(display-mode: standalone)');
    const on = () => setStandalone(currentStandalone());
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  const show = useCallback(
    (from: 'auto' | 'menu') => {
      saveState(markShown(loadState(), Date.now()));
      if (from === 'auto') rememberShownThisSession();
      setSource(from);
      setOpen(true);
      trackUiEvent('pwa.install.shown', { platform, mode, source: from });
    },
    [platform, mode],
  );

  // Automatic sheet after sign-in.
  useEffect(() => {
    const decision = shouldAutoPrompt({
      state: loadState(),
      now: Date.now(),
      platform,
      standalone,
      shownThisSession: shownThisSession(),
    });
    if (!decision.show) return undefined;
    const t = window.setTimeout(() => show('auto'), AUTO_PROMPT_DELAY_MS);
    return () => window.clearTimeout(t);
    // Once per mount (sign-in); the menu entry covers later requests.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "Install app" in the user menu: phones and tablets, or any browser that
  // offered its install prompt; never inside the installed app.
  const offerInMenu = !standalone && !pwa.installedNow && (mobile || pwa.deferredPrompt !== null);
  useEffect(() => {
    if (!offerInMenu) return undefined;
    return registerUserMenuItem({
      id: 'pwa.install',
      label: 'Install app',
      order: 60,
      onSelect: () => show('menu'),
    });
  }, [offerInMenu, show]);

  // A new build is waiting: one persistent toast with Reload.
  useEffect(() => {
    if (!pwa.updateWaiting) return undefined;
    const id = toast({
      title: 'New version available',
      description: 'Reload to use the latest Velora.',
      duration: Number.POSITIVE_INFINITY,
      action: { label: 'Reload', onAction: applyUpdate },
    });
    return () => toast.dismiss(id);
  }, [pwa.updateWaiting]);

  const notNow = () => {
    setOpen(false);
    saveState(dismiss(loadState(), Date.now()));
    trackUiEvent('pwa.install.outcome', { outcome: 'dismissed', platform, mode, source });
  };
  const acknowledge = () => {
    setOpen(false);
    // Instructions read; the same 7-day rest before it shows by itself again.
    saveState(dismiss(loadState(), Date.now()));
    trackUiEvent('pwa.install.outcome', { outcome: 'instructions', platform, mode, source });
  };
  const install = () => {
    setOpen(false);
    void promptInstall().then((outcome) => {
      if (outcome === 'accepted') saveState(markInstalled(loadState()));
      else saveState(dismiss(loadState(), Date.now()));
      trackUiEvent('pwa.install.outcome', { outcome, platform, mode, source });
    });
  };

  if (standalone) return null;
  return (
    <InstallSheet
      open={open}
      mode={mode}
      device={isTablet(device) ? 'tablet' : mobile ? 'phone' : 'device'}
      onInstall={install}
      onNotNow={notNow}
      onAcknowledge={acknowledge}
    />
  );
}
